import React from 'react';
import { StyleSheet, View } from './components/base_components';

import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import ProgressSpinner from './components/progress_spinner';
import OrgTabBar from './components/org_tab_bar';
import TextButton from './components/buttons/text_button';
import StandardBoxList from './components/standard_box_list';

import OrgStore from './stores/org_store';
import UserStore from './stores/user_store';

import { byteFormat } from './tools/util';

const DEFAULT_MAX_SIZE = '4398046511104';

const styles = StyleSheet.create({
  orgS3Screen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  actionButton: { marginLeft: '1rem' },
  spinner: { margin: '3rem', alignSelf: 'center' },
  tabBar: { marginLeft: '2.2rem', marginRight: '2.2rem' },
});

export default function OrgS3Screen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const org_id = UserStore.useOrg()?.org_id;
  const org = OrgStore.useOrg(org_id);
  const [selected, setSelected] = React.useState<number[]>([]);
  const [new_s3_bucket, setNewS3Bucket] = React.useState<any>({});
  const selected_bucket = org?.s3_bucket_list?.[selected?.[0]];
  const bucket_count = org?.s3_bucket_list?.length;

  React.useEffect(() => {
    OrgStore.fetch(org_id);
  }, [org_id]);

  function _onCreateBucket(complete) {
    const opts = Object.assign({ org_id }, new_s3_bucket);
    opts.max_size = parseFloat(opts.max_size || DEFAULT_MAX_SIZE);
    if (setBusy()) {
      OrgStore.addBucket(opts, (err) => {
        if (err === 412) {
          window.alert(
            'Failed to validate bucket, please check your settings.'
          );
        } else if (err) {
          window.alert('Failed to add bucket, please try again.');
        } else {
          complete(null);
        }
        clearBusy();
      });
    }
  }
  function _flipBuckets(a, b, success) {
    if (a && b && setBusy()) {
      const update_list = [
        { s3_bucket_id: a.s3_bucket_id, priority: b.priority },
        { s3_bucket_id: b.s3_bucket_id, priority: a.priority },
      ];
      const opts = { org_id, update_list };
      OrgStore.orderBuckets(opts, (err) => {
        if (err) {
          window.alert('Failed to reorder, please try again.');
        } else {
          success();
        }
        clearBusy();
      });
    }
  }
  function _onMoveUpPress() {
    const previous_index = selected?.[0] - 1;
    const previous = org?.s3_bucket_list?.[previous_index];
    _flipBuckets(previous, selected_bucket, () => {
      setSelected([previous_index]);
    });
  }
  function _onMoveDownPress() {
    const next_index = selected?.[0] + 1;
    const next = org?.s3_bucket_list?.[next_index];
    _flipBuckets(next, selected_bucket, () => {
      setSelected([next_index]);
    });
  }

  const buttons = (
    <>
      <TextButton
        style={styles.actionButton}
        text='Move Up'
        disabled={!selected_bucket || selected[0] === 0}
        onPress={_onMoveUpPress}
      />
      <TextButton
        style={styles.actionButton}
        text='Move Down'
        disabled={!selected_bucket || selected[0] === bucket_count - 1}
        onPress={_onMoveDownPress}
      />
    </>
  );

  const rows = org?.s3_bucket_list?.map?.((bucket, i) => ({
    url: `/org/bucket/${bucket.s3_bucket_id}`,
    list: [
      bucket.s3_bucket,
      bucket.max_size ? byteFormat(bucket.max_size) : '-',
      bucket.priority ? i + 1 : '-',
      bucket.is_disabled ? 'Disabled' : 'Enabled',
    ],
  }));
  let content: any = null;
  if (!org) {
    content = <ProgressSpinner style={styles.spinner} color='#555' />;
  } else {
    content = (
      <>
        <Breadcrumb
          list={[{ text: org?.org_name, url: `/org` }, 'Network Stores']}
        />
        <OrgTabBar style={styles.tabBar} />
        <StandardBoxList
          title='Network Stores'
          headers={['Name', 'Max File Size', 'Priority', 'Status']}
          buttons={buttons}
          rows={rows}
          singleSelect={true}
          selected={selected}
          onChange={setSelected}
        />
        <BoxPropList
          title='Add Network Store'
          editable={true}
          alwaysEdit={true}
          updates={new_s3_bucket}
          onChange={setNewS3Bucket}
          onSave={_onCreateBucket}
          columns={[
            [
              {
                key: 's3_bucket',
                label: 'Bucket Name',
                value: '',
                input: { placeholder: 'bucket' },
                required: true,
              },
              {
                key: 's3_bucket_region',
                label: 'S3 Region',
                value: '',
                input: { placeholder: 'region' },
                required: true,
              },
              {
                key: 's3_host',
                label: 'S3 Host',
                value: '',
                input: { placeholder: 'host' },
                required: true,
              },
              {
                key: 's3_bucket_prefix',
                label: 'S3 Bucket Prefix',
                value: '',
                input: { placeholder: 'prefix (optional)' },
              },
              {
                key: 'aws_access_key_id',
                label: 'Access Key ID',
                value: '',
                input: { placeholder: 'access key id' },
                required: true,
              },
              {
                key: 'aws_secret_access_key',
                label: 'Secret Access Key',
                value: '',
                input: { placeholder: 'secret access key' },
                required: true,
              },
              {
                key: 'max_size',
                label: 'Max Object Size',
                value: DEFAULT_MAX_SIZE,
                input: { placeholder: 'bytes', type: 'number' },
                required: true,
              },
            ],
          ]}
        />
      </>
    );
  }
  return (
    <View style={styles.orgS3Screen}>
      {content}
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
